/**
 * Browser-side export helpers: embed the site's fonts into an SVG so it
 * renders the same anywhere, rasterize SVG to a canvas or PNG, and trigger
 * downloads. Used by the certificate and merchandise pages.
 */
import jostUrl from '@fontsource-variable/jost/files/jost-latin-wght-normal.woff2?url'
import garamondUrl from '@fontsource/eb-garamond/files/eb-garamond-latin-400-normal.woff2?url'
import poppinsUrl from '@fontsource/poppins/files/poppins-latin-300-normal.woff2?url'
import { CERTIFICATE_SIZE } from './certificate'

async function toDataUrl(url: string): Promise<string> {
  const res = await fetch(url)
  const buf = await res.arrayBuffer()
  let bin = ''
  const bytes = new Uint8Array(buf)
  for (let i = 0; i < bytes.length; i += 0x8000) bin += String.fromCharCode(...bytes.subarray(i, i + 0x8000))
  return `data:font/woff2;base64,${btoa(bin)}`
}

let fontCssCache: Promise<string> | null = null

/** @font-face rules for Jost (variable), EB Garamond, and Poppins Light with the files inlined. */
export function certificateFontCss(): Promise<string> {
  if (!fontCssCache) {
    fontCssCache = Promise.all([toDataUrl(jostUrl), toDataUrl(garamondUrl), toDataUrl(poppinsUrl)]).then(
      ([jost, garamond, poppins]) => `
@font-face{font-family:'Jost Variable';font-style:normal;font-weight:100 900;src:url(${jost}) format('woff2');}
@font-face{font-family:'EB Garamond';font-style:normal;font-weight:400;src:url(${garamond}) format('woff2');}
@font-face{font-family:'Poppins';font-style:normal;font-weight:300;src:url(${poppins}) format('woff2');}
`,
    )
  }
  return fontCssCache
}

export interface Size {
  width: number
  height: number
}

/** Render an SVG string onto a canvas of the given pixel size, on white (or `background`). */
export function renderSvg(svg: string, size: Size, background = '#ffffff'): Promise<HTMLCanvasElement> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(new Blob([svg], { type: 'image/svg+xml;charset=utf-8' }))
    const img = new Image()
    img.onload = () => {
      try {
        const canvas = document.createElement('canvas')
        canvas.width = Math.round(size.width)
        canvas.height = Math.round(size.height)
        const ctx = canvas.getContext('2d')
        if (!ctx) throw new Error('canvas unavailable')
        if (background) {
          ctx.fillStyle = background
          ctx.fillRect(0, 0, canvas.width, canvas.height)
        }
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height)
        resolve(canvas)
      } catch (e) {
        reject(e)
      } finally {
        URL.revokeObjectURL(url)
      }
    }
    img.onerror = () => {
      URL.revokeObjectURL(url)
      reject(new Error('svg render failed'))
    }
    img.src = url
  })
}

export function canvasToPng(canvas: HTMLCanvasElement): Promise<Blob> {
  return new Promise((resolve, reject) =>
    canvas.toBlob((blob) => (blob ? resolve(blob) : reject(new Error('png encode failed'))), 'image/png'),
  )
}

/** Render an SVG string to a PNG blob; the certificate's native size by default. */
export async function svgToPng(svg: string, scale = 1, size: Size = CERTIFICATE_SIZE): Promise<Blob> {
  return canvasToPng(await renderSvg(svg, { width: size.width * scale, height: size.height * scale }))
}

export function downloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  a.remove()
  setTimeout(() => URL.revokeObjectURL(url), 1000)
}
